/**
 * Smart Shikka AI Core — PR91
 * No-Runtime End-to-End Safety Audit Pack Registry
 * Audit Metadata Only — No Execution
 */
function deepFreeze(obj) {
    if (obj === null || obj === undefined)
        return obj;
    Object.freeze(obj);
    if (Array.isArray(obj)) {
        for (const item of obj)
            deepFreeze(item);
    }
    else if (typeof obj === "object") {
        for (const val of Object.values(obj)) {
            if (typeof val === "object" && val !== null)
                deepFreeze(val);
        }
    }
    return obj;
}
const BLOCKED_ACTIONS = [
    { action: "approval_execute", reason: "No approval execution in PR91" },
    { action: "child_process", reason: "No child process in PR91" },
    { action: "message_send", reason: "No message send in PR91" },
    { action: "old_assistant_alias", reason: "No old assistant alias in PR91" },
    { action: "provider_call", reason: "No provider call in PR91" },
    { action: "runtime_action_execute", reason: "No runtime action execution in PR91" },
    { action: "runtime_unlock", reason: "No runtime unlock in PR91" },
    { action: "secret_literal", reason: "No secret literals in PR91" },
    { action: "shell_command", reason: "No shell command in PR91" },
    { action: "unapproved_db_write", reason: "No unapproved DB write in PR91" },
    { action: "unapproved_file_write", reason: "No unapproved file write in PR91" },
    { action: "unapproved_network_call", reason: "No unapproved network call in PR91" },
    { action: "unknown_provider_call", reason: "No unknown provider call in PR91" },
    { action: "unknown_tool_execution", reason: "No unknown tool execution in PR91" },
    { action: "unredacted_secret", reason: "No unredacted secret in PR91" },
    { action: "unsafe_env_dump", reason: "No unsafe env dump in PR91" },
    { action: "action_execute", reason: "No action execution in PR91" },
    { action: "agent_execution", reason: "No agent execution in PR91" },
    { action: "workflow_execution", reason: "No workflow execution in PR91" },
    { action: "tool_execution", reason: "No tool execution in PR91" },
    { action: "gate_unlock", reason: "No gate unlock in PR91" },
    { action: "file_read", reason: "No file read in PR91" },
    { action: "file_write", reason: "No file write in PR91" },
    { action: "consent_persist", reason: "No consent persistence in PR91" },
    { action: "role_mutation", reason: "No role mutation in PR91" },
    { action: "permission_mutation", reason: "No permission mutation in PR91" },
    { action: "state_persist", reason: "No state persistence in PR91" },
    { action: "local_storage_write", reason: "No localStorage write in PR91" },
    { action: "session_storage_write", reason: "No sessionStorage write in PR91" },
    { action: "indexed_db_write", reason: "No IndexedDB write in PR91" },
    { action: "sqlite_write", reason: "No SQLite write in PR91" },
    { action: "db_write", reason: "No DB write in PR91" },
    { action: "supabase_call", reason: "No Supabase call in PR91" },
    { action: "chat_runtime_start", reason: "No chat runtime start in PR91" },
    { action: "model_inference", reason: "No model inference in PR91" },
    { action: "render_document", reason: "No document render in PR91" },
    { action: "inject_icon_runtime", reason: "No icon runtime injection in PR91" },
    { action: "browser_action", reason: "No browser automation in PR91" },
    { action: "desktop_action", reason: "No desktop automation in PR91" },
    { action: "run_pake_command", reason: "No Pake command execution in PR91" },
    { action: "network_call", reason: "No network call in PR91" },
    { action: "secret_read", reason: "No secret read in PR91" },
    { action: "attachment_parse", reason: "No attachment parsing in PR91" },
    { action: "upload_file", reason: "No file upload in PR91" },
    { action: "user_drive_access", reason: "No user drive access in PR91" },
];
const SAFETY_DEFAULTS = {
    previewOnly: true,
    readonlyOnly: true,
    auditExecutionAllowedInPr91: false,
    runtimeActionAllowedInPr91: false,
    runtimeUnlockAllowedInPr91: false,
    providerCallAllowedInPr91: false,
    toolExecutionAllowedInPr91: false,
    agentExecutionAllowedInPr91: false,
    workflowExecutionAllowedInPr91: false,
    messageSendAllowedInPr91: false,
    chatRuntimeAllowedInPr91: false,
    documentRenderAllowedInPr91: false,
    iconInjectionAllowedInPr91: false,
    browserAutomationAllowedInPr91: false,
    desktopAutomationAllowedInPr91: false,
    pakeExecutionAllowedInPr91: false,
    smartPrintDriveAccessAllowedInPr91: false,
    fileMutationAllowedInPr91: false,
    networkAllowedInPr91: false,
    dbWriteAllowedInPr91: false,
    localStorageAllowedInPr91: false,
    sessionStorageAllowedInPr91: false,
    indexedDbAllowedInPr91: false,
    sqliteAllowedInPr91: false,
    supabaseAllowedInPr91: false,
    secretAccessAllowedInPr91: false,
    approvalExecutionAllowedInPr91: false,
    approvalPersistenceAllowedInPr91: false,
    roleMutationAllowedInPr91: false,
    permissionMutationAllowedInPr91: false,
    modelInferenceAllowedInPr91: false,
    attachmentParsingAllowedInPr91: false,
    gateUnlockAllowedInPr91: false,
    requiresOwnerApproval: true,
    requiresUserApproval: true,
};
const registry = [
    {
        id: "preview_chain_audit_summary",
        displayName: "Smart Shikka Assistant Preview Chain Audit Summary",
        description: "End-to-end audit summary of the PR84-PR90 preview chain: route → UI → chat → conversation state → approval state → action request → execution gate",
        sourceModules: ["smart-shikka-assistant-orchestrator", "smart-shikka-assistant-ui-route-preview", "smart-shikka-assistant-readonly-chat-preview", "smart-shikka-assistant-readonly-conversation-state-preview", "smart-shikka-assistant-readonly-approval-state-preview", "smart-shikka-assistant-readonly-action-request-preview", "smart-shikka-assistant-readonly-execution-gate-matrix-preview"],
        targetPreviewModules: ["smart-shikka-assistant-no-runtime-safety-audit-pack"],
        relatedRouteIds: ["document_render_request", "workflow_plan_request", "icon_identity_request", "capability_discovery_request", "safety_guardrail_explain_request", "desktop_shell_plan_request", "smart_print_local_data_plan_request", "source_adoption_audit_request", "audit_report_request", "future_agent_task_request"],
        relatedUiPreviewIds: ["assistant_home_panel", "route_picker_panel"],
        relatedChatPreviewIds: ["readonly_conversation_shell", "readonly_message_thread_preview"],
        relatedConversationStatePreviewIds: ["readonly_conversation_state_snapshot"],
        relatedApprovalStatePreviewIds: ["readonly_approval_summary_state"],
        relatedActionRequestPreviewIds: ["readonly_action_request_summary"],
        ...SAFETY_DEFAULTS,
        blockedActions: BLOCKED_ACTIONS,
        denialReasons: ["PR91 is audit-only — no runtime action allowed", "End-to-end chain audit produces metadata only"],
        futurePrScope: ["PR92+ may enable per-gate runtime unlock with dual approval"],
        notes: ["Proves PR84-PR90 chain is connected and still no-runtime"],
    },
    {
        id: "route_to_ui_audit",
        displayName: "Smart Shikka Assistant Route-to-UI Audit",
        description: "Audit proving PR84 orchestrator routes map to PR85 UI previews",
        sourceModules: ["smart-shikka-assistant-orchestrator", "smart-shikka-assistant-ui-route-preview"],
        targetPreviewModules: ["smart-shikka-assistant-no-runtime-safety-audit-pack"],
        relatedRouteIds: ["document_render_request", "workflow_plan_request", "icon_identity_request", "capability_discovery_request", "safety_guardrail_explain_request", "desktop_shell_plan_request", "smart_print_local_data_plan_request", "source_adoption_audit_request", "audit_report_request", "future_agent_task_request"],
        relatedUiPreviewIds: ["assistant_home_panel", "route_picker_panel", "document_render_route_card", "workflow_plan_route_card", "icon_identity_route_card", "capability_discovery_route_card", "safety_guardrail_explain_card", "desktop_shell_plan_route_card", "smart_print_local_data_plan_card", "source_adoption_audit_card", "audit_report_route_card", "future_agent_task_blocked_card"],
        relatedChatPreviewIds: [],
        relatedConversationStatePreviewIds: [],
        relatedApprovalStatePreviewIds: [],
        relatedActionRequestPreviewIds: [],
        ...SAFETY_DEFAULTS,
        blockedActions: BLOCKED_ACTIONS,
        denialReasons: ["Audit only — no UI rendering allowed in PR91"],
        futurePrScope: ["Future PR may enable UI rendering with owner approval"],
        notes: ["PR84 routes → PR85 UI preview cards: metadata mapping verified"],
    },
    {
        id: "ui_to_chat_audit",
        displayName: "Smart Shikka Assistant UI-to-Chat Audit",
        description: "Audit proving PR85 UI previews map to PR86 chat previews",
        sourceModules: ["smart-shikka-assistant-ui-route-preview", "smart-shikka-assistant-readonly-chat-preview"],
        targetPreviewModules: ["smart-shikka-assistant-no-runtime-safety-audit-pack"],
        relatedRouteIds: [],
        relatedUiPreviewIds: ["assistant_home_panel", "route_picker_panel"],
        relatedChatPreviewIds: ["readonly_conversation_shell", "readonly_message_thread_preview", "user_message_draft_preview", "assistant_response_placeholder", "route_context_badge_panel", "safety_warning_panel", "blocked_message_send_card", "provider_call_blocked_card", "tool_execution_blocked_card", "attachment_placeholder_readonly_card", "audit_transcript_preview", "future_chat_runtime_blocked_card"],
        relatedConversationStatePreviewIds: [],
        relatedApprovalStatePreviewIds: [],
        relatedActionRequestPreviewIds: [],
        ...SAFETY_DEFAULTS,
        blockedActions: BLOCKED_ACTIONS,
        denialReasons: ["Audit only — no chat runtime allowed in PR91"],
        futurePrScope: ["Future PR may enable chat runtime with dual approval"],
        notes: ["PR85 UI → PR86 chat: metadata mapping verified"],
    },
    {
        id: "chat_to_conversation_state_audit",
        displayName: "Smart Shikka Assistant Chat-to-Conversation-State Audit",
        description: "Audit proving PR86 chat previews map to PR87 conversation state previews",
        sourceModules: ["smart-shikka-assistant-readonly-chat-preview", "smart-shikka-assistant-readonly-conversation-state-preview"],
        targetPreviewModules: ["smart-shikka-assistant-no-runtime-safety-audit-pack"],
        relatedRouteIds: [],
        relatedUiPreviewIds: [],
        relatedChatPreviewIds: ["readonly_conversation_shell", "blocked_message_send_card", "provider_call_blocked_card"],
        relatedConversationStatePreviewIds: ["readonly_conversation_state_snapshot", "readonly_selected_route_state", "readonly_message_draft_state", "readonly_message_thread_state", "readonly_response_placeholder_state", "readonly_route_context_state", "readonly_safety_warning_state", "readonly_blocked_runtime_state", "readonly_attachment_placeholder_state", "readonly_audit_transcript_state", "readonly_ui_selection_state", "readonly_owner_approval_state", "readonly_user_approval_state", "future_persistent_conversation_state_blocked"],
        relatedApprovalStatePreviewIds: [],
        relatedActionRequestPreviewIds: [],
        ...SAFETY_DEFAULTS,
        blockedActions: BLOCKED_ACTIONS,
        denialReasons: ["Audit only — no conversation persistence allowed in PR91"],
        futurePrScope: ["Future PR may enable conversation state persistence with approval"],
        notes: ["PR86 chat → PR87 conversation state: metadata mapping verified"],
    },
    {
        id: "conversation_state_to_approval_audit",
        displayName: "Smart Shikka Assistant Conversation-State-to-Approval Audit",
        description: "Audit proving PR87 conversation state previews map to PR88 approval state previews",
        sourceModules: ["smart-shikka-assistant-readonly-conversation-state-preview", "smart-shikka-assistant-readonly-approval-state-preview"],
        targetPreviewModules: ["smart-shikka-assistant-no-runtime-safety-audit-pack"],
        relatedRouteIds: [],
        relatedUiPreviewIds: [],
        relatedChatPreviewIds: [],
        relatedConversationStatePreviewIds: ["readonly_conversation_state_snapshot", "readonly_owner_approval_state", "readonly_user_approval_state"],
        relatedApprovalStatePreviewIds: ["readonly_approval_summary_state", "readonly_owner_approval_required_state", "readonly_user_approval_required_state", "readonly_dual_approval_required_state", "readonly_approval_denied_state", "readonly_approval_pending_state", "readonly_blocked_role_mutation_state", "readonly_blocked_permission_mutation_state", "readonly_blocked_runtime_unlock_state", "readonly_provider_call_approval_blocked_state", "readonly_tool_execution_approval_blocked_state", "readonly_file_access_approval_blocked_state", "readonly_persistence_approval_blocked_state", "readonly_approval_audit_evidence_placeholder", "readonly_approval_policy_explain_state", "future_approval_runtime_blocked_state"],
        relatedActionRequestPreviewIds: [],
        ...SAFETY_DEFAULTS,
        blockedActions: BLOCKED_ACTIONS,
        denialReasons: ["Audit only — no approval execution allowed in PR91"],
        futurePrScope: ["Future PR may enable approval runtime with role-based access"],
        notes: ["PR87 conversation state → PR88 approval state: metadata mapping verified"],
    },
    {
        id: "approval_to_action_request_audit",
        displayName: "Smart Shikka Assistant Approval-to-Action-Request Audit",
        description: "Audit proving PR88 approval state previews map to PR89 action request previews",
        sourceModules: ["smart-shikka-assistant-readonly-approval-state-preview", "smart-shikka-assistant-readonly-action-request-preview"],
        targetPreviewModules: ["smart-shikka-assistant-no-runtime-safety-audit-pack"],
        relatedRouteIds: [],
        relatedUiPreviewIds: [],
        relatedChatPreviewIds: [],
        relatedConversationStatePreviewIds: [],
        relatedApprovalStatePreviewIds: ["readonly_approval_summary_state", "readonly_provider_call_approval_blocked_state", "readonly_tool_execution_approval_blocked_state"],
        relatedActionRequestPreviewIds: ["readonly_action_request_summary", "readonly_action_request_from_route", "readonly_action_request_from_chat_draft", "readonly_action_request_requires_owner_approval", "readonly_action_request_requires_user_approval", "readonly_action_request_requires_dual_approval", "readonly_provider_action_request_blocked", "readonly_tool_action_request_blocked", "readonly_agent_action_request_blocked", "readonly_workflow_action_request_blocked", "readonly_document_render_action_request_blocked", "readonly_icon_injection_action_request_blocked", "readonly_file_access_action_request_blocked", "readonly_browser_action_request_blocked", "readonly_desktop_action_request_blocked", "readonly_smart_print_local_data_action_request_blocked", "readonly_audit_action_request_placeholder", "future_action_execution_runtime_blocked"],
        ...SAFETY_DEFAULTS,
        blockedActions: BLOCKED_ACTIONS,
        denialReasons: ["Audit only — no action execution allowed in PR91"],
        futurePrScope: ["Future PR may enable action execution with per-action approval"],
        notes: ["PR88 approval state → PR89 action request: metadata mapping verified"],
    },
    {
        id: "action_request_to_execution_gate_audit",
        displayName: "Smart Shikka Assistant Action-Request-to-Execution-Gate Audit",
        description: "Audit proving PR89 action request previews map to PR90 execution gate entries",
        sourceModules: ["smart-shikka-assistant-readonly-action-request-preview", "smart-shikka-assistant-readonly-execution-gate-matrix-preview"],
        targetPreviewModules: ["smart-shikka-assistant-no-runtime-safety-audit-pack"],
        relatedRouteIds: [],
        relatedUiPreviewIds: [],
        relatedChatPreviewIds: [],
        relatedConversationStatePreviewIds: [],
        relatedApprovalStatePreviewIds: [],
        relatedActionRequestPreviewIds: ["readonly_action_request_summary", "readonly_provider_action_request_blocked", "readonly_tool_action_request_blocked", "future_action_execution_runtime_blocked"],
        ...SAFETY_DEFAULTS,
        blockedActions: BLOCKED_ACTIONS,
        denialReasons: ["Audit only — no gate unlock allowed in PR91"],
        futurePrScope: ["Future PR may enable per-gate unlock with dual approval and audit trail"],
        notes: ["PR89 action request → PR90 execution gate: metadata mapping verified"],
    },
    {
        id: "blocked_runtime_action_audit",
        displayName: "Smart Shikka Assistant Blocked Runtime Action Audit",
        description: "Audit of all blocked runtime actions across the PR84-PR90 chain",
        sourceModules: ["smart-shikka-assistant-no-runtime-safety-audit-pack"],
        targetPreviewModules: [],
        relatedRouteIds: [],
        relatedUiPreviewIds: [],
        relatedChatPreviewIds: [],
        relatedConversationStatePreviewIds: [],
        relatedApprovalStatePreviewIds: [],
        relatedActionRequestPreviewIds: [],
        ...SAFETY_DEFAULTS,
        blockedActions: BLOCKED_ACTIONS,
        denialReasons: ["All runtime actions blocked in PR84-PR91 chain", "45 blocked action categories enforced"],
        futurePrScope: ["Future PR may selectively unblock actions with per-action approval"],
        notes: ["Comprehensive blocked action audit across all preview modules"],
    },
    {
        id: "old_alias_naming_audit",
        displayName: "Smart Shikka Assistant Old Alias Naming Audit",
        description: "Audit proving 0 old assistant alias occurrences across PR84-PR91 chain",
        sourceModules: ["smart-shikka-assistant-no-runtime-safety-audit-pack"],
        targetPreviewModules: [],
        relatedRouteIds: [],
        relatedUiPreviewIds: [],
        relatedChatPreviewIds: [],
        relatedConversationStatePreviewIds: [],
        relatedApprovalStatePreviewIds: [],
        relatedActionRequestPreviewIds: [],
        ...SAFETY_DEFAULTS,
        blockedActions: BLOCKED_ACTIONS,
        denialReasons: ["Old assistant alias is banned", "Only Smart Shikka Assistant / AI / Core naming allowed"],
        futurePrScope: [],
        notes: ["Grep scan: 0 occurrences of old alias in all code/docs/reports"],
    },
    {
        id: "package_export_audit",
        displayName: "Smart Shikka Assistant Package Export Audit",
        description: "Audit proving all PR84-PR91 module subpath exports are registered",
        sourceModules: ["smart-shikka-assistant-no-runtime-safety-audit-pack"],
        targetPreviewModules: [],
        relatedRouteIds: [],
        relatedUiPreviewIds: [],
        relatedChatPreviewIds: [],
        relatedConversationStatePreviewIds: [],
        relatedApprovalStatePreviewIds: [],
        relatedActionRequestPreviewIds: [],
        ...SAFETY_DEFAULTS,
        blockedActions: BLOCKED_ACTIONS,
        denialReasons: ["Package export audit is metadata only"],
        futurePrScope: [],
        notes: ["Verifies subpath exports in packages/core/package.json and re-exports in packages/core/src/index.ts"],
    },
    {
        id: "docs_report_audit",
        displayName: "Smart Shikka Assistant Docs and Report Audit",
        description: "Audit proving all PR84-PR91 docs and reports exist and contain required safety statements",
        sourceModules: ["smart-shikka-assistant-no-runtime-safety-audit-pack"],
        targetPreviewModules: [],
        relatedRouteIds: [],
        relatedUiPreviewIds: [],
        relatedChatPreviewIds: [],
        relatedConversationStatePreviewIds: [],
        relatedApprovalStatePreviewIds: [],
        relatedActionRequestPreviewIds: [],
        ...SAFETY_DEFAULTS,
        blockedActions: BLOCKED_ACTIONS,
        denialReasons: ["Docs/report audit is metadata only"],
        futurePrScope: [],
        notes: ["Verifies PR84_REPORT.md through PR91_REPORT.md and all docs/*.md files"],
    },
    {
        id: "future_runtime_handoff_audit",
        displayName: "Smart Shikka Assistant Future Runtime Handoff Audit",
        description: "Audit placeholder for future runtime handoff — all gates remain locked",
        sourceModules: ["smart-shikka-assistant-no-runtime-safety-audit-pack", "smart-shikka-assistant-readonly-execution-gate-matrix-preview"],
        targetPreviewModules: [],
        relatedRouteIds: [],
        relatedUiPreviewIds: [],
        relatedChatPreviewIds: [],
        relatedConversationStatePreviewIds: [],
        relatedApprovalStatePreviewIds: [],
        relatedActionRequestPreviewIds: ["future_action_execution_runtime_blocked"],
        ...SAFETY_DEFAULTS,
        blockedActions: BLOCKED_ACTIONS,
        denialReasons: ["Future runtime handoff not started", "All gates locked in PR91"],
        futurePrScope: ["PR92+ may begin per-gate runtime handoff planning", "Requires owner + user dual approval per gate"],
        notes: ["This entry is a placeholder — no runtime handoff in PR91"],
    },
];
deepFreeze(registry);
export function getSmartShikkaAssistantNoRuntimeSafetyAuditPackEntries() {
    return registry;
}
export function getSmartShikkaAssistantNoRuntimeSafetyAuditPackEntryById(id) {
    return registry.find((e) => e.id === id);
}
//# sourceMappingURL=smartShikkaAssistantNoRuntimeSafetyAuditPackRegistry.js.map